<script setup lang="ts">
  import { ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { ArrowLeft } from 'lucide-vue-next'
  import Button from '@/components/ui/button/Button.vue'
  import FileField from '@/components/admin/form/FileField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { PdfFormTemplatesService } from '@/types/generated'
  import { ROUTES } from '@/router/routes'

  function getCsrfToken(): string | undefined {
    const value = `; ${document.cookie}`
    const parts = value.split(`; csrftoken=`)
    if (parts.length === 2) return parts.pop()?.split(';').shift()
  }

  const router = useRouter()

  const form = ref({
    name: 'New document template',
    original_pdf: null as File | null
  })

  const errors = ref<Record<string, string>>({})
  const loading = ref(false)

  const handleFileChange = (file: File | null) => {
    form.value.original_pdf = file
    if (file) {
      errors.value.original_pdf = ''
    }
  }

  const goBack = () => {
    router.push(ROUTES.APP.SETTINGS.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES)
  }

  const createTemplate = async () => {
    if (!form.value.original_pdf || !form.value.name.trim()) {
      if (!form.value.name.trim()) {
        errors.value.name = 'Name is required'
      }
      if (!form.value.original_pdf) {
        errors.value.original_pdf = 'PDF file is required'
      }
      return
    }

    errors.value = {}

    try {
      loading.value = true

      const formData = new FormData()
      formData.append('name', form.value.name.trim())
      formData.append('original_pdf', form.value.original_pdf)

      const csrfToken = getCsrfToken()
      const headers: HeadersInit = {}
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken
      }

      const response = await fetch('/api/v3/pdf_form_templates/', {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(JSON.stringify(errorData))
      }

      const responseData = await response.json()

      const templateId = responseData.uuid || responseData.id
      if (templateId) {
        router.push(
          `${ROUTES.APP.SETTINGS.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES}/${templateId}/edit`
        )
      } else {
        console.error('No UUID/ID in response:', responseData)
        router.push(ROUTES.APP.SETTINGS.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES)
      }
    } catch (error: any) {
      console.error('Failed to create PDF form template:', error)
    } finally {
      loading.value = false
    }
  }
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6">
      <div class="flex items-center gap-4">
        <button
          class="flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-gray-900"
          @click="goBack"
        >
          <ArrowLeft class="h-4 w-4" />
          Back to PDF Form Templates
        </button>
      </div>
    </div>

    <!-- Main content -->
    <div class="p-6">
      <div class="mx-auto max-w-4xl">
        <div class="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
          <div class="space-y-8">
            <!-- Form Header -->
            <div>
              <h2 class="mb-2 text-lg font-medium text-gray-900">Template Information</h2>
              <p class="text-sm text-gray-600">Configure your PDF form template settings</p>
            </div>

            <!-- Template Name -->
            <div>
              <InputField
                v-model="form.name"
                name="name"
                label="Template Name"
                placeholder="Enter template name"
              />
              <div
                v-if="errors.name"
                class="mt-1 text-sm text-red-600"
              >
                {{ errors.name }}
              </div>
            </div>

            <!-- PDF Upload -->
            <div>
              <FileField
                name="original_pdf"
                label="PDF Document"
                :allowedFileExtensions="['pdf']"
                @update:model-value="handleFileChange"
              />
              <div
                v-if="errors.original_pdf"
                class="mt-2 text-sm text-red-600"
              >
                {{ errors.original_pdf }}
              </div>
            </div>

            <!-- Create Button -->
            <div class="flex justify-end">
              <Button
                :disabled="loading || !form.original_pdf || !form.name.trim()"
                class="flex items-center gap-2"
                @click="createTemplate"
              >
                {{ loading ? 'Creating...' : 'Create Template' }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
